import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { OrderStateServiceV2 } from '../../order-state.service';
import { OrderCartData } from '../../../../../../models/order/workflow/order-cart.model';
import { InternetCartStateService } from './order-internet-cart.state.service';
import { WirelessCartStateService } from './order-wireless-cart.state.service';
import { VideoCartStateService } from './order-video-cart.state.service';
import { VoiceCartStateService } from './order-voice-cart.state.service';


@Injectable({ providedIn: 'root' })
export class CartStateService {
    private orderStateService = inject(OrderStateServiceV2);
    private internetCartStateService = inject(InternetCartStateService);
    private wirelessCartStateService = inject(WirelessCartStateService);
    private videoCartStateService = inject(VideoCartStateService);
    private voiceCartStateService = inject(VoiceCartStateService);
    private _cart = signal<OrderCartData>({
        firstBillCharges: { items: [], totalAmount: 0 },
        todaysCharges: { items: [], totalAmount: 0 },
        packagesCharges: { items: [], totalAmount: 0 },
        savingAndPromotionCharges: { items: [], totalAmount: 0 },
        internetCharges: undefined,
        wirelessCharges: undefined,
        videoCharges: undefined,
        voiceCharges: undefined,
        monthlyTotal: 0,
        totalAmount: 0
    });

    cart = this._cart.asReadonly();

    constructor() {
        effect(() => {
            const order = this.orderStateService.order();
            this._cart.set(null);
            if (order) {
                this.setupCart();
            }
        });
    }

    private setupCart() {
        const internetCart = this.internetCartStateService.cart();
        const wirelessCart = this.wirelessCartStateService.cart();
        const videoCart = this.videoCartStateService.cart();
        const voiceCart = this.voiceCartStateService.cart();
        if (!internetCart && !wirelessCart && !videoCart && !voiceCart) {
            return;
        }

        const cartData: OrderCartData = {
            firstBillCharges: { items: [], totalAmount: 0 },
            todaysCharges: { items: [], totalAmount: 0 },
            packagesCharges: { items: [], totalAmount: 0 },
            savingAndPromotionCharges: { items: [], totalAmount: 0 },
            internetCharges: undefined,
            wirelessCharges: undefined,
            videoCharges: undefined,
            voiceCharges: undefined,
            monthlyTotal: 0,
            totalAmount: 0
        }

        if (internetCart) {
            //fix  no-unsafe-optional-chaining
            cartData.internetCharges = internetCart;
            cartData.firstBillCharges.items.push(...(internetCart.firstBillCharges?.items || []));
            cartData.firstBillCharges.totalAmount = (cartData.firstBillCharges.totalAmount) + (internetCart.firstBillCharges?.totalAmount || 0);

            cartData.todaysCharges.items.push(...(internetCart.todaysCharges?.items || []));
            cartData.todaysCharges.totalAmount = (cartData.todaysCharges.totalAmount) + (internetCart.todaysCharges?.totalAmount || 0);

            cartData.packagesCharges.items.push(...(internetCart.packagesCharges?.items || []));
            cartData.packagesCharges.totalAmount = (cartData.packagesCharges.totalAmount) + (internetCart.packagesCharges?.totalAmount || 0);

            cartData.savingAndPromotionCharges.items.push(...(internetCart.savingAndPromotionCharges?.items || []));
            cartData.savingAndPromotionCharges.totalAmount = (cartData.savingAndPromotionCharges.totalAmount) + (internetCart.savingAndPromotionCharges?.totalAmount || 0);

            cartData.monthlyTotal += internetCart.monthly?.totalAmount || 0;
        }

        if (wirelessCart) {
            cartData.wirelessCharges = wirelessCart;
            cartData.firstBillCharges.items.push(...(wirelessCart.firstBillCharges?.items || []));
            cartData.firstBillCharges.totalAmount = (cartData.firstBillCharges.totalAmount) + (wirelessCart.firstBillCharges?.totalAmount || 0);

            cartData.todaysCharges.items.push(...(wirelessCart.todaysCharges?.items || []));
            cartData.todaysCharges.totalAmount = (cartData.todaysCharges.totalAmount) + (wirelessCart.todaysCharges?.totalAmount || 0);

            cartData.packagesCharges.items.push(...(wirelessCart.packagesCharges?.items || []));
            cartData.packagesCharges.totalAmount = (cartData.packagesCharges.totalAmount) + (wirelessCart.packagesCharges?.totalAmount || 0);

            cartData.savingAndPromotionCharges.items.push(...(wirelessCart.savingAndPromotionCharges?.items || []));
            cartData.savingAndPromotionCharges.totalAmount = (cartData.savingAndPromotionCharges.totalAmount) + (wirelessCart.savingAndPromotionCharges?.totalAmount || 0);

            cartData.monthlyTotal += wirelessCart.totalMonthlyAmount || 0;
        }

        if (videoCart) {
            cartData.videoCharges = videoCart;
            cartData.firstBillCharges.items.push(...(videoCart.firstBillCharges?.items || []));
            cartData.firstBillCharges.totalAmount = (cartData.firstBillCharges.totalAmount) + (videoCart.firstBillCharges?.totalAmount || 0);

            cartData.todaysCharges.items.push(...(videoCart.todaysCharges?.items || []));
            cartData.todaysCharges.totalAmount = (cartData.todaysCharges.totalAmount) + (videoCart.todaysCharges?.totalAmount || 0);

            cartData.packagesCharges.items.push(...(videoCart.packagesCharges?.items || []));
            cartData.packagesCharges.totalAmount = (cartData.packagesCharges.totalAmount) + (videoCart.packagesCharges?.totalAmount || 0);

            cartData.savingAndPromotionCharges.items.push(...(videoCart.savingAndPromotionCharges?.items || []));
            cartData.savingAndPromotionCharges.totalAmount = (cartData.savingAndPromotionCharges.totalAmount) + (videoCart.savingAndPromotionCharges?.totalAmount || 0);

            cartData.monthlyTotal += videoCart.monthly?.totalAmount || 0;
        }

        if (voiceCart) {
            cartData.voiceCharges = voiceCart;
            cartData.firstBillCharges.items.push(...(voiceCart.firstBillCharges?.items || []));
            cartData.firstBillCharges.totalAmount = (cartData.firstBillCharges.totalAmount) + (voiceCart.firstBillCharges?.totalAmount || 0);

            cartData.todaysCharges.items.push(...(voiceCart.todaysCharges?.items || []));
            cartData.todaysCharges.totalAmount = (cartData.todaysCharges.totalAmount) + (voiceCart.todaysCharges?.totalAmount || 0);

            cartData.packagesCharges.items.push(...(voiceCart.packagesCharges?.items || []));
            cartData.packagesCharges.totalAmount = (cartData.packagesCharges.totalAmount) + (voiceCart.packagesCharges?.totalAmount || 0);

            cartData.savingAndPromotionCharges.items.push(...(voiceCart.savingAndPromotionCharges?.items || []));
            cartData.savingAndPromotionCharges.totalAmount = (cartData.savingAndPromotionCharges.totalAmount) + (voiceCart.savingAndPromotionCharges?.totalAmount || 0);

            cartData.monthlyTotal += voiceCart.monthly?.totalAmount || 0;
        }

        this._cart.set(cartData);
    }

    isCartEmpty = computed(() => {
        const cart = this.cart();
        return !cart && (this.internetCartStateService.isCartEmpty() && this.wirelessCartStateService.isCartEmpty() && this.videoCartStateService.isCartEmpty() && this.voiceCartStateService.isCartEmpty());
    });

    canBeVisible = computed(() => {
        const order = this.orderStateService.order();
        return order?.internetFlow != null || order?.wirelessFlow != null || order?.videoFlow != null || order?.voiceFlow != null;
    });

    getTotalTodaysCharges(skipInternet: boolean): number {
        const internetTodaysCharges = skipInternet ? 0 : this.internetCartStateService.getTotalTodaysCharges();
        const wirelessTodaysCharges = this.wirelessCartStateService.getTotalTodaysCharges();
        const videoTodaysCharges = this.videoCartStateService.getTotalTodaysCharges();
        const voiceTodaysCharges = this.voiceCartStateService.getTotalTodaysCharges();

        return internetTodaysCharges + wirelessTodaysCharges + videoTodaysCharges + voiceTodaysCharges;
    }
}
